import { action, makeObservable, observable, runInAction } from 'mobx';
import { setupMobX } from '../../../utils';
import {
  requestMeasurementData,
  requestMeasurementDataWithToken,
  requestFormulaUpdateExecution,
  requestMeasurementList,
} from '../backend';
import {
  MeasurementData,
  MeasurementDataWithIdAndDate,
  MeasurementsDto,
} from './reportsTypes';
import { roundMeasurementData } from '../util';
import { jwtDecode } from 'jwt-decode';
import { PaymentTokenDto, Parameters } from '@web/common';

export class ReportsStore {
  @observable public formulaUpdateNeeded = false;
  @observable public latestFormulaCreatedAt: Date | undefined;
  @observable public loadingMeasurementList = false;
  @observable public loadedMeasurementList = false;
  @observable public loadingMeasurementData = false;
  @observable public loadedMeasurementData = false;
  @observable public failedLoadingMeasurementList = false;
  @observable public measurements: MeasurementsDto | undefined;
  @observable public measurementData:
    | MeasurementDataWithIdAndDate[]
    | undefined;

  constructor() {
    makeObservable(this);
  }

  @action public async loadMeasurementData(
    measurementId: string,
    measurementDate: string,
    kcToken: string,
  ) {
    try {
      const response = await requestMeasurementData(measurementId, kcToken);
      const roundedResponse = roundMeasurementData(response);
      console.debug('✅ Loaded measurement data successfully');
      return {
        id: measurementId,
        date: measurementDate,
        dataSet: roundedResponse,
      };
    } catch (error) {
      console.error('❌ Loading measurement data failed:', error);
    }
  }

  @action public async loadAllMeasurements(kcToken: string) {
    if (!this.loadingMeasurementData) {
      runInAction(() => {
        this.loadingMeasurementData = true;
        this.loadedMeasurementData = false;
      });
      try {
        await this.loadMeasurementList(kcToken);
        if (this.measurements) {
          const promises = this.measurements.map((entry) => {
            return this.loadMeasurementData(
              entry.id,
              entry.timestamp,
              kcToken,
            ).catch((error) => {
              console.log(
                'failed to load data for entry with id: ' + entry.id,
                error,
              );
            });
          });

          const results = await Promise.all(promises);
          const measurementData = results.filter(
            (entry) => entry !== undefined,
          );
          runInAction(() => {
            if (results) {
              this.measurementData = measurementData;
            }
            this.loadedMeasurementData = true;
          });
        }
      } catch (error) {
        console.log('Error fetching data', error);
      } finally {
        runInAction(() => (this.loadingMeasurementData = false));
      }
    }
  }

  @action public async loadMeasurementFromPaymentToken(paymentToken: string) {
    if (!this.loadingMeasurementData) {
      runInAction(() => {
        this.loadingMeasurementList = true;
        this.loadingMeasurementData = true;
      });
      const tokenData = jwtDecode(paymentToken) as PaymentTokenDto;
      runInAction(() => {
        this.measurements = [
          {
            id: tokenData.id,
            timestamp: new Date(tokenData.iat).toISOString(),
          },
        ];
        this.loadedMeasurementList = true;
        this.loadingMeasurementList = false;
      });
      try {
        const response = await requestMeasurementDataWithToken(paymentToken);
        const roundedResponse = roundMeasurementData(response);

        runInAction(() => {
          this.measurementData = [
            {
              id: tokenData.id,
              date: tokenData.iat.toString(),
              dataSet: roundedResponse,
            },
          ];
          this.loadedMeasurementData = true;
        });
      } catch (error) {
        console.error('failed to load measurement data.', error);
        runInAction(() => {
          this.loadingMeasurementData = false;
          this.failedLoadingMeasurementList = true;
        });
      } finally {
        runInAction(() => {
          this.loadingMeasurementData = false;
        });
      }
    }
  }

  @action public getMeasurementFromId(id: string) {
    return this.measurementData?.find((entry) => entry.id === id);
  }

  @action public isLatestMeasurement(id: string) {
    if (this.measurements) {
      const targetIndex = this.measurements.length - 1;
      const indexOfId = this.measurements.findIndex((entry) => entry.id === id);
      return targetIndex === indexOfId;
    } else return false;
  }

  @action public getParameterUnit(parameter: Parameters) {
    if (this.measurementData) {
      return this.measurementData[0].dataSet[parameter as keyof MeasurementData]
        .unit;
    } else return '';
  }

  @action public getParameterThresholds(parameter: Parameters) {
    if (this.measurementData) {
      return this.measurementData[0].dataSet[parameter as keyof MeasurementData]
        .thresholds;
    } else return [];
  }

  @action public getValueParameterValues(
    parameter: Parameters,
    parameter2?: Parameters,
  ) {
    if (this.measurementData) {
      return this.measurementData.map((entry) => {
        return {
          date: new Date(entry.date),
          series1: entry.dataSet[parameter as keyof MeasurementData].value,
          series2: parameter2
            ? entry.dataSet[parameter2 as keyof MeasurementData].value
            : 0,
        };
      });
    } else {
      return [];
    }
  }

  @action public getComparisonParameterValues(
    parameter: Parameters,
    parameter2: Parameters,
  ) {
    if (this.measurementData) {
      return this.measurementData?.map((entry) => {
        return {
          date: new Date(entry.date),
          series: [
            entry.dataSet[parameter as keyof MeasurementData].value,
            entry.dataSet[parameter2 as keyof MeasurementData]?.value,
          ],
        };
      });
    } else {
      return [];
    }
  }

  @action public getCategoryParameterValues(parameter: Parameters) {
    if (this.measurementData) {
      return this.measurementData.map((entry) => {
        return {
          date: new Date(entry.date),
          series: entry.dataSet[parameter as keyof MeasurementData].value,
        };
      });
    } else {
      return [];
    }
  }

  @action public async updateFormulas(kcToken: string) {
    try {
      const response = await requestFormulaUpdateExecution(kcToken);
      console.debug('✅ Triggered formula update successfully:', response);
    } catch (error) {
      console.error('❌ Triggering formula update failed:', error);
    }
  }

  @action public async loadMeasurementList(kcToken: string) {
    if (!this.loadingMeasurementList) {
      runInAction(() => {
        this.loadingMeasurementList = true;
        this.loadedMeasurementList = false;
      });
      try {
        const response = await requestMeasurementList(kcToken);
        console.debug('✅ Loaded measurement list successfully');
        runInAction(() => {
          this.measurements = response.measurements
            .reverse()
            .map(({ id, timestamp }) => ({
              id,
              timestamp: new Date(timestamp).toISOString(),
            }));
          this.loadedMeasurementList = true;
          this.formulaUpdateNeeded = response.containsOutdated;
          this.latestFormulaCreatedAt = response.latestFormulaCreatedAt;
        });
      } catch (error) {
        console.error('❌ Loading measurement list failed:', error);
        runInAction(() => (this.failedLoadingMeasurementList = true));
      } finally {
        runInAction(() => (this.loadingMeasurementList = false));
      }
    }
  }
}

const { provider, useStore } = setupMobX<ReportsStore>();
export const ReportsProvider = provider;
export const useReportsStore = useStore;
